/**
 * "Your password expires in N days."
 *
 * One component for the two places it appears — the banner across the portal
 * and the panel on the profile page — because the rule about when a note
 * becomes an alert is the kind that gets changed once and copied never.
 *
 * It renders nothing at all outside the warning window. A permanent readout of
 * a password that expires in eleven weeks is noise, and noise is what makes the
 * one that matters invisible.
 */

import { Link } from 'react-router-dom';
import { isUrgent, shouldWarn, PASSWORD_URGENT_DAYS } from '@parinaam-vms/shared';
import type { PasswordStatus } from '@parinaam-vms/shared';
import styles from './PasswordNotice.module.css';

interface Props {
  status: PasswordStatus;
  /** The profile page suppresses its own link — the form is right there. */
  withLink?: boolean;
}

/**
 * The date, in the form a person reads rather than an ISO string.
 *
 * `en-IN`, and long-form month: "11 January 2027" cannot be misread the way
 * 11/01/2027 can by anyone who has ever used an American system.
 */
function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso));
}

export function PasswordNotice({ status, withLink = true }: Props) {
  if (!shouldWarn(status) || status.expires_at === null || status.days_remaining === null) {
    return null;
  }

  const urgent = isUrgent(status);
  const days = status.days_remaining;

  // Past the date, and still signed in only because the session outlives the
  // password. The forced-change screen normally catches this first; this is the
  // wording if it is ever reached another way.
  const when =
    days < 0
      ? 'Your password has expired.'
      : days === 0
        ? 'Your password expires today.'
        : days === 1
          ? 'Your password expires tomorrow.'
          : `Your password expires in ${days} days.`;

  return (
    <div
      className={`${styles.notice} ${urgent ? styles.urgent : styles.soon}`}
      // An alert is announced the moment it appears; a note waits its turn.
      // Five days left is worth interrupting a screen reader for, eleven is not.
      role={urgent ? 'alert' : 'status'}
    >
      <span className={styles.text}>
        <strong>{when}</strong> It must be changed by {formatDate(status.expires_at)}.
      </span>
      {withLink && (
        <Link className={styles.action} to="/profile">
          Change it now
        </Link>
      )}
    </div>
  );
}

/** Exported for the tests, which assert the threshold rather than trust it. */
export const URGENT_BELOW_DAYS = PASSWORD_URGENT_DAYS;
